import React, {useCallback} from 'react';
import {useAppContext} from 'contexts/ApplicationContext';
import {Accordion} from 'shared/components/Accordion';
import {useRenderGateway} from 'components/customer/Confirmation/helpers/useRenderGateway';
import {GatewayCode} from 'components/manufacturer/Payments/entity/Gateway';
import {Alert, Col, Container as BSContainer, Row} from 'react-bootstrap';
import {CBCButton} from 'shared/helpers';
import styled from 'styled-components';
import {useNavigate} from 'react-router-dom';
import {useJobContext} from 'contexts/JobContext';
import {PartialJob} from 'shared/types/PartialJob';
import {useAppDispatch, useAppSelector} from 'store/customer';
import {
    cardTypeSet,
    paymentMethodSet,
    paymentTypeSet,
    selectAccountPayAvailable,
    selectErrors,
    selectGatewayAvailable,
    selectInvoiceAvailable,
    selectPaymentMethod,
    selectPaymentType,
    selectPreviousPaymentMethod,
} from 'components/customer/Confirmation/store/confirmationSlice';
import {shallowEqual} from 'react-redux';
import {PaymentType} from 'components/manufacturer/Payments/entity/Deposit';
import {parseHtmlString} from 'shared/helpers/HTMLParser';
import {PaymentOptions} from 'components/customer/Confirmation/Payment/PaymentOptions';
import {PaymentMethodType} from 'components/manufacturer/Payments/entity/Setting';
import Icon from 'shared/Icon';
import {usePaymentContext} from 'components/customer/Confirmation/Payment';
import {PDFReportButtons} from 'components/customer/Confirmation/PDFReportButtons';
import {createMediaQuery, mobile, smallDevices} from 'shared/Breakpoints';
import {CardType} from 'components/customer/Confirmation/Payment/entity/PaymentSession';
import {isMobile, isTablet} from 'react-device-detect';
import {ContactDetails} from 'components/customer/Confirmation/Payment/ContactDetails';

const capitalizeFirstLetter = (text: string) => {
    return text.charAt(0).toUpperCase() + text.slice(1);
};

const largeDesktops = createMediaQuery('(min-width: 1369px)');

export const PaymentGateways = () => {
    const {isBalancePayment} = usePaymentContext();
    const dispatch = useAppDispatch();
    const {job} = useJobContext() as PartialJob;
    const navigate = useNavigate();
    const {userProfile} = useAppContext();

    const paymentMethod = useAppSelector(selectPaymentMethod);
    const paymentType = useAppSelector(selectPaymentType);
    const errorMessages = useAppSelector(selectErrors, shallowEqual);
    const isGatewayAvailable = useAppSelector(selectGatewayAvailable);
    const isInvoiceAvailable = useAppSelector(selectInvoiceAvailable);
    const isAccountPayAvailable = useAppSelector(selectAccountPayAvailable);
    const previousPaymentMethod = useAppSelector(selectPreviousPaymentMethod);

    const {renderPaymentGatewayElements} = useRenderGateway();

    const handlePaymentOptionSelect = useCallback(
        (key: string) => {
            if (key == PaymentType.INVOICE) {
                dispatch(paymentTypeSet(PaymentType.INVOICE));
                dispatch(cardTypeSet(CardType.invoice));

                if (paymentMethod == PaymentMethodType.CARD_DEPOSIT) {
                    dispatch(
                        paymentMethodSet(PaymentMethodType.INVOICE_DEPOSIT)
                    );
                } else {
                    dispatch(paymentMethodSet(PaymentMethodType.INVOICE_FULL));
                }
            } else if (key == PaymentType.GATEWAY) {
                dispatch(cardTypeSet(CardType.card_domestic));
                dispatch(paymentTypeSet(PaymentType.GATEWAY));

                if (paymentMethod == PaymentMethodType.INVOICE_DEPOSIT) {
                    dispatch(paymentMethodSet(PaymentMethodType.CARD_DEPOSIT));
                } else {
                    dispatch(paymentMethodSet(PaymentMethodType.CARD_FULL));
                }
            } else {
                dispatch(paymentTypeSet(PaymentType.ACCOUNT));
                dispatch(paymentMethodSet(PaymentMethodType.ACCOUNT));
                dispatch(cardTypeSet(CardType.account));
            }
        },
        [paymentMethod]
    );

    const isPreviousPaymentAccount =
        previousPaymentMethod == null || previousPaymentMethod == 'account';

    const handleBackButton = useCallback(() => {
        if (isBalancePayment) {
            void navigate(`/v2`);
            return;
        }

        if (userProfile.jobConfirmationEnabled) {
            void navigate(`/v2/job/${job.displayId}/confirmation`);
        } else {
            void navigate(`/v2/job/${job.displayId}/dashboard`);
        }
    }, [job, navigate, userProfile, isBalancePayment]);

    return (
        <ResponsiveContainer>
            <ButtonContainer>
                <PDFReportButtons />
                <CBCButton
                    type="submit"
                    iconName="Button-Previous.svg"
                    className="job-button button-blue"
                    onClick={handleBackButton}>
                    {isBalancePayment
                        ? 'Back to homepage'
                        : 'Back to Order details'}
                </CBCButton>

                <ContactDetails show={!!(isMobile && !isTablet)} />
            </ButtonContainer>
            <Container>
                <Row>
                    <Col md={{span: 5, offset: 1}}>
                        <div className="pe-5 payment-options">
                            <br />
                            {errorMessages && errorMessages.length > 0 ? (
                                <>
                                    {errorMessages.map((errorMessage) => {
                                        return (
                                            <React.Fragment key={errorMessage}>
                                                <ErrorMessage variant="danger">
                                                    {parseHtmlString(
                                                        errorMessage
                                                    )}
                                                </ErrorMessage>
                                                <br />
                                            </React.Fragment>
                                        );
                                    })}
                                </>
                            ) : null}

                            <Accordion
                                defaultActiveKey={paymentType}
                                onSelect={handlePaymentOptionSelect}>
                                {isGatewayAvailable &&
                                previousPaymentMethod != 'account'
                                    ? userProfile?.availablePaymentGateways.map(
                                          (gateway) => {
                                              return (
                                                  <Accordion.Item
                                                      key={gateway.name}
                                                      eventKey={'gateway'}>
                                                      <Accordion.Header>
                                                          <div>
                                                              {gateway.name ==
                                                              GatewayCode.STRIPE
                                                                  ? 'Credit or debit card'
                                                                  : capitalizeFirstLetter(
                                                                        gateway.name
                                                                    )}
                                                          </div>
                                                          <PaymentMethodIcon iconName="Credit-Card.svg" />
                                                      </Accordion.Header>
                                                      <Accordion.Body>
                                                          {renderPaymentGatewayElements(
                                                              gateway
                                                          )}
                                                      </Accordion.Body>
                                                  </Accordion.Item>
                                              );
                                          }
                                      )
                                    : null}

                                {isInvoiceAvailable &&
                                previousPaymentMethod != 'account' ? (
                                    <Accordion.Item
                                        eventKey={PaymentType.INVOICE}>
                                        <Accordion.Header>
                                            <div>Invoice</div>
                                            <PaymentMethodIcon iconName="Invoice-Primary.svg" />
                                        </Accordion.Header>
                                        <Accordion.Body>
                                            <p>
                                                An invoice will be emailed to
                                                you with payment information.
                                            </p>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                ) : null}

                                {isAccountPayAvailable &&
                                !isBalancePayment &&
                                isPreviousPaymentAccount ? (
                                    <Accordion.Item
                                        eventKey={PaymentType.ACCOUNT}>
                                        <Accordion.Header>
                                            <div>Account</div>
                                            <PaymentMethodIcon iconName="Account.svg" />
                                        </Accordion.Header>
                                        <Accordion.Body>
                                            <p>
                                                We will process your payment
                                                directly.
                                            </p>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                ) : null}
                            </Accordion>

                            <ContactDetails show={!isMobile || isTablet} />
                        </div>
                    </Col>
                    <Col md={{span: 5}}>
                        <br />
                        <PaymentOptions />
                    </Col>
                </Row>
            </Container>
        </ResponsiveContainer>
    );
};

const ResponsiveContainer = styled.div`
    display: flex;
    flex-direction: column;

    ${mobile`
        flex-direction: column-reverse;

        > div:first-child {
            align-items: center;
            margin-top: 15px;
            height: fit-content;

            > div, > button, > a {
                width: 100%;
            }
        }

        .col-md-5, .payment-options {
            padding: 0 !important;
        }
    `}

    ${smallDevices`
        flex-direction: column-reverse;

        > div:first-child {
            align-items: center;
            margin-top: 15px;
            height: fit-content;

            > div, > button, > a {
                width: 100%;
            }
        }

        .row {
            margin: 0;
            gap: 15px;

            > .col-md-5:last-child {
                flex: 1; 
            }
        }

        .col-md-5, .payment-options {
            margin-left: 0;
            padding: 0 !important;
        }
    `}

    ${largeDesktops`
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        column-gap: 30px;
        align-items: start;
        max-width: 1550px;
        margin: 0 auto;

        > div:first-child {
            grid-column: 2;
            grid-row: 1;
            justify-self: end;
        }

        > div:last-child {
            grid-column: 1;
            grid-row: 1;
        }

        .row {
            margin: 0;
            gap: 30px;
            flex-wrap: nowrap;
        }

        .col-md-5 {
            flex: 1;
            max-width: 100%;
            width: auto;
            margin-left: 0;
        }

        .col-md-5:first-child {
            padding-left: 0;
        }

        .payment-options {
            position: relative;
            left: 20px;
        }

        .col-md-5:last-child {
            position: relative;
            right: 20px;
        }
    `}
`;

const Container = styled(BSContainer)`
    margin-top: -25px;

    ${smallDevices`
        padding: 0;
    `}

    ${largeDesktops`
        margin-top: 0;
        max-width: none;
        margin-left: 0;
        margin-right: 0;
        padding-left: 0;
    `}
`;

const PaymentMethodIcon = styled(Icon)`
    width: 30px;
`;

const ButtonContainer = styled.div`
    position: relative;
    z-index: 1;
    height: 0;
    display: flex;
    flex-direction: column;
    gap: 15px;
    margin-top: 30px;
    align-items: end;

    > div,
    > button,
    > a {
        width: 14vw;
    }

    ${smallDevices`
        flex-direction: row-reverse;    
    `}

    ${largeDesktops`
        position: relative;
        right: 20px;
        z-index: auto;
        height: fit-content;
        flex-direction: column;
        align-items: stretch;
        margin-top: 23px;

        > div, > button, > a {
            width: 190px;
        }
    `}
`;

const ErrorMessage = styled(Alert)`
    border-radius: 8px;
    padding: 20px;
    background: ${({theme}) => theme.colors.error.main};
    color: #a90000;
`;
